import { createContext, useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, Lock } from "lucide-react";
import { api } from "@/lib/api";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const FIELDS = [
  "tipo", "nombre", "nacionalidad", "rfc", "curp", "ine_clave_elector", "pasaporte", "documento_migratorio",
  "tax_id_extranjero", "razon_social", "rfc_moral", "representante_legal_nombre", "instrumento_constitutivo",
  "domicilio", "correo", "whatsapp",
];

const SENS = new Set(["rfc", "curp", "ine_clave_elector", "pasaporte", "documento_migratorio", "tax_id_extranjero", "rfc_moral"]);
const RevealCtx = createContext({ reveal: () => {}, ocultos: [] });

export function LockNote() {
  return (
    <p className="text-xs text-[#2A5C55] bg-[#EDF5F3] rounded-lg px-3 py-2 flex items-start gap-2" data-testid="lock-note">
      <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" /> Estos datos se guardan cifrados. Nadie más los ve; solo se usan para llenar tus documentos.
    </p>
  );
}

function F({ label, name, value, onChange, missing, type = "text", placeholder }) {
  const { reveal, ocultos } = useContext(RevealCtx);
  const sens = SENS.has(name) && String(value || "").includes("****");
  const oculto = ocultos.includes(name);
  return (
    <div>
      <label className="text-xs font-semibold text-slate-600 mb-1 block">
        {label}{missing && <span className="ml-1 text-[#B45309]">· falta</span>}{oculto && <span className="ml-1 text-slate-400">· nunca se muestra</span>}
      </label>
      <div className="relative">
        <input type={type} value={value || ""} onChange={(e) => onChange(name, e.target.value)} placeholder={placeholder}
               onFocus={(e) => { if (sens) e.target.select(); }} data-testid={`perfil-${name}`}
               className={`w-full h-12 px-3 ${sens && !oculto ? "pr-11" : ""} rounded-xl border bg-white outline-none focus:ring-2 focus:ring-[#1E3A5F] ${missing ? "border-[#F59E0B] bg-[#FFFBEB]" : "border-[#CBD5E1]"}`} />
        {sens && !oculto && (
          <button type="button" onClick={() => reveal(name, value)} className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-slate-500" data-testid={`perfil-reveal-${name}`} aria-label="Ver un momento">
            <Eye className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}

function Opt({ label, name, value, onChange, options }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-600 mb-1 block">{label}</label>
      <Select value={value || ""} onValueChange={(v) => onChange(name, v)}>
        <SelectTrigger className="h-12" data-testid={`perfil-${name}`}><SelectValue placeholder="Selecciona" /></SelectTrigger>
        <SelectContent>{options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

function IdentidadFields({ f, set, miss }) {
  if (f.tipo === "moral") {
    return (
      <>
        <F label="Razón social" name="razon_social" value={f.razon_social} onChange={set} missing={miss("Razón social")} />
        <div className="grid sm:grid-cols-2 gap-3">
          <F label="RFC" name="rfc_moral" value={f.rfc_moral} onChange={set} missing={miss("RFC")} />
          <F label="Representante legal" name="representante_legal_nombre" value={f.representante_legal_nombre} onChange={set} missing={miss("Representante legal")} />
        </div>
        <F label="Instrumento constitutivo" name="instrumento_constitutivo" value={f.instrumento_constitutivo} onChange={set} missing={miss("Instrumento constitutivo")} placeholder="Escritura pública No…" />
      </>
    );
  }
  if (f.nacionalidad === "extranjera") {
    return (
      <div className="grid sm:grid-cols-2 gap-3">
        <F label="Pasaporte" name="pasaporte" value={f.pasaporte} onChange={set} missing={miss("Pasaporte")} />
        <F label="Documento migratorio" name="documento_migratorio" value={f.documento_migratorio} onChange={set} missing={miss("Documento migratorio")} />
        <F label="RFC (si tiene)" name="rfc" value={f.rfc} onChange={set} />
        <F label="Tax ID extranjero (opcional)" name="tax_id_extranjero" value={f.tax_id_extranjero} onChange={set} />
      </div>
    );
  }
  return (
    <div className="grid sm:grid-cols-3 gap-3">
      <F label="RFC" name="rfc" value={f.rfc} onChange={set} missing={miss("RFC")} />
      <F label="CURP" name="curp" value={f.curp} onChange={set} missing={miss("CURP")} />
      <F label="Clave de elector (INE)" name="ine_clave_elector" value={f.ine_clave_elector} onChange={set} missing={miss("Clave de elector")} />
    </div>
  );
}

export default function PerfilForm({ perfil, onSaved, submitLabel = "Guardar perfil" }) {
  const [f, setF] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    const p = perfil?.persona || {};
    setF(Object.fromEntries(FIELDS.map((k) => [k, p[k] ?? ""])));
  }, [perfil]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const faltantes = perfil?.faltantes || [];
  const reveal = async (name, masked) => {
    try {
      const { data } = await api.get(`/perfil/revelar/${name}`);
      set(name, data.valor);
      setTimeout(() => setF((p) => (p[name] === data.valor ? { ...p, [name]: masked } : p)), 8000);
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo mostrar"); }
  };

  const save = async () => {
    const body = { ...f, tipo: f.tipo || "fisica", nacionalidad: f.nacionalidad || "mexicana" };
    if (body.tipo === "moral" && !body.nombre) body.nombre = body.razon_social;
    if (!String(body.nombre || "").trim()) return toast.error("Ingresa tu nombre completo");
    setSaving(true);
    try {
      const { data } = await api.put("/perfil", body);
      toast.success("Perfil guardado");
      onSaved?.(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo guardar el perfil");
    } finally { setSaving(false); }
  };

  return (
    <RevealCtx.Provider value={{ reveal, ocultos: perfil?.ocultos || [] }}>
    <div className="space-y-4" data-testid="perfil-form">
      <div className="grid sm:grid-cols-2 gap-3">
        <Opt label="Tipo de persona" name="tipo" value={f.tipo || "fisica"} onChange={set}
             options={[{ value: "fisica", label: "Persona física" }, { value: "moral", label: "Persona moral" }]} />
        {f.tipo !== "moral" && (
          <Opt label="Nacionalidad" name="nacionalidad" value={f.nacionalidad || "mexicana"} onChange={set}
               options={[{ value: "mexicana", label: "Mexicana" }, { value: "extranjera", label: "Extranjera" }]} />
        )}
      </div>
      <F label={f.tipo === "moral" ? "Nombre comercial" : "Nombre completo"} name="nombre" value={f.nombre} onChange={set}
         missing={faltantes.includes("Nombre completo") && !String(f.nombre || "").trim()} />
      <IdentidadFields f={f} set={set} miss={(l) => faltantes.includes(l)} />
      <LockNote />
      <F label="Domicilio" name="domicilio" value={f.domicilio} onChange={set} missing={faltantes.includes("Domicilio")} placeholder="Calle, número, colonia, ciudad, estado, CP" />
      <div className="grid sm:grid-cols-2 gap-3">
        <F label="Correo" name="correo" type="email" value={f.correo} onChange={set} missing={faltantes.includes("Correo")} />
        <F label="WhatsApp" name="whatsapp" value={f.whatsapp} onChange={set} missing={faltantes.includes("WhatsApp")} />
      </div>
      <button onClick={save} disabled={saving} className="bm-btn-primary w-full sm:w-auto" data-testid="perfil-save-btn">
        {saving ? "Guardando…" : submitLabel}
      </button>
    </div>
    </RevealCtx.Provider>
  );
}
